/**
 * EL TUTORIAL INTERACTIVO.
 *
 * Oscurece la pantalla, recorta un "agujero" sobre el elemento del que está
 * hablando y pone un cartel al lado. No es un video ni una pantalla aparte: el
 * usuario está mirando **su propia aplicación, con sus propios datos**, que es
 * la única forma de que lo aprendido se le quede.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  TRES DECISIONES QUE LO HACEN ROBUSTO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 1. **Si el elemento no está, el paso no se rompe.** Un negocio con el catálogo
 *    vacío no tiene grilla de productos que señalar; en ese caso el cartel se
 *    muestra centrado. Un tutorial que se cuelga es peor que no tener tutorial.
 *
 * 2. **Los pasos se filtran por permiso y por configuración.** A un empleado no
 *    se le enseña la pantalla de Reportes, que no puede abrir; a un negocio de
 *    una sola moneda no se le explica la tasa del día.
 *
 * 3. **El agujero se hace con una sombra gigante**, no con cuatro rectángulos ni
 *    con `clip-path`: `box-shadow: 0 0 0 9999px negro` pinta todo MENOS el
 *    recuadro. Una línea de CSS en vez de cuatro divs que hay que recolocar.
 */

import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, GraduationCap, X } from "lucide-react";
import { Boton, cx } from "@/components/ui";
import { monedas } from "@/lib/endpoints";
import { useAuth } from "@/stores/auth";
import { useTutorial } from "@/stores/tutorial";
import { PASOS, type PasoTutorial } from "./pasos";

/** Cuánto respiro se deja alrededor del elemento señalado. */
const MARGEN = 8;

type Recuadro = { top: number; left: number; width: number; height: number };

export function Tutorial() {
  const { puede } = useAuth();
  const navegar = useNavigate();
  const ubicacion = useLocation();

  const abierto = useTutorial((e) => e.abierto);
  const paso = useTutorial((e) => e.paso);
  const { cerrar, siguiente, anterior } = useTutorial();

  const { data: misMonedas } = useQuery({
    queryKey: ["monedas-negocio"],
    queryFn: monedas.listar,
    enabled: abierto && puede("currency.view"),
  });

  const multimoneda = (misMonedas ?? []).filter((m) => m.is_active).length > 1;

  // Los pasos que ESTE usuario debe ver, en ESTE negocio.
  const pasos = PASOS.filter((p) => {
    if (p.permiso && !puede(p.permiso)) return false;
    if (p.soloMultimoneda && !multimoneda) return false;
    return true;
  });

  const actual: PasoTutorial | undefined = pasos[Math.min(paso, pasos.length - 1)];

  const [recuadro, setRecuadro] = useState<Recuadro | null>(null);

  // --- Llevar al usuario a la pantalla del paso ---------------------------
  useEffect(() => {
    if (!abierto || !actual) return;
    if (ubicacion.pathname !== actual.ruta) navegar(actual.ruta);
  }, [abierto, actual, ubicacion.pathname, navegar]);

  // --- Medir el elemento a señalar ----------------------------------------
  const medir = useCallback(() => {
    if (!abierto || !actual?.objetivo) {
      setRecuadro(null);
      return;
    }

    const nodo = document.querySelector<HTMLElement>(`[data-tour="${actual.objetivo}"]`);
    if (!nodo) {
      // El elemento no está en esta pantalla (catálogo vacío, permiso, tamaño
      // de pantalla…). El cartel se muestra centrado y el paso sigue sirviendo.
      setRecuadro(null);
      return;
    }

    const r = nodo.getBoundingClientRect();
    setRecuadro({
      top: r.top - MARGEN,
      left: r.left - MARGEN,
      width: r.width + MARGEN * 2,
      height: r.height + MARGEN * 2,
    });
  }, [abierto, actual]);

  useLayoutEffect(() => {
    if (!abierto) return;

    // Un respiro para que la pantalla nueva termine de dibujarse antes de medir.
    const t = window.setTimeout(medir, 220);

    window.addEventListener("resize", medir);
    window.addEventListener("scroll", medir, true);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", medir);
      window.removeEventListener("scroll", medir, true);
    };
  }, [abierto, medir, ubicacion.pathname]);

  // --- Salir con Escape ----------------------------------------------------
  useEffect(() => {
    if (!abierto) return;
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === "Escape") cerrar();
      if (e.key === "ArrowRight" || e.key === "Enter") siguiente(pasos.length);
      if (e.key === "ArrowLeft") anterior();
    };
    window.addEventListener("keydown", alPulsar);
    return () => window.removeEventListener("keydown", alPulsar);
  }, [abierto, cerrar, siguiente, anterior, pasos.length]);

  if (!abierto || !actual) return null;

  const esUltimo = paso >= pasos.length - 1;

  return (
    <div className="fixed inset-0 z-[100]" role="dialog" aria-modal aria-label="Recorrido guiado">
      {/*
        El velo. Cuando hay recuadro se dibuja como una sombra enorme alrededor
        del hueco; cuando no, es un velo normal a pantalla completa.
      */}
      {recuadro ? (
        <div
          className="pointer-events-none absolute rounded-[var(--radius-card)] transition-all duration-300"
          style={{
            top: recuadro.top,
            left: recuadro.left,
            width: recuadro.width,
            height: recuadro.height,
            boxShadow: "0 0 0 9999px rgb(3 18 24 / 0.72)",
            outline: "2px solid var(--c-accent)",
            outlineOffset: 2,
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-[rgb(3_18_24/0.72)]" onClick={cerrar} />
      )}

      {/* El cartel */}
      <div
        className={cx(
          "animar-entrada absolute w-[min(24rem,calc(100vw-2rem))] rounded-[var(--radius-modal)]",
          "border border-line bg-surface p-5 shadow-[var(--shadow-pop)]",
        )}
        style={posicionDelCartel(recuadro)}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <span className="flex items-center gap-2 text-xs font-semibold text-accent">
            <GraduationCap size={15} />
            Paso {paso + 1} de {pasos.length}
          </span>
          <button
            type="button"
            onClick={cerrar}
            aria-label="Salir del recorrido"
            className="-mr-1.5 -mt-1.5 rounded-[var(--radius-control)] p-1.5 text-muted transition-colors hover:bg-surface-hover hover:text-ink"
          >
            <X size={16} />
          </button>
        </div>

        <h2 className="titulo-seccion">{actual.titulo}</h2>
        <p className="mt-2 text-sm leading-relaxed text-ink-soft">{actual.texto}</p>

        {/* Barra de avance */}
        <div className="mt-4 flex gap-1">
          {pasos.map((_, i) => (
            <div
              key={i}
              className={cx(
                "h-1 flex-1 rounded-full transition-colors",
                i <= paso ? "bg-accent" : "bg-line",
              )}
            />
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={cerrar}
            className="text-xs text-muted transition-colors hover:text-ink"
          >
            Salir del recorrido
          </button>

          <div className="flex gap-2">
            {paso > 0 && (
              <Boton
                variante="secundario"
                tamano="sm"
                icono={<ArrowLeft size={15} />}
                onClick={anterior}
              >
                Atrás
              </Boton>
            )}
            <Boton
              tamano="sm"
              icono={!esUltimo ? <ArrowRight size={15} /> : undefined}
              onClick={() => siguiente(pasos.length)}
            >
              {esUltimo ? "Terminar" : "Siguiente"}
            </Boton>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Dónde poner el cartel: debajo del elemento si cabe, encima si no, y centrado
 * cuando no hay nada que señalar.
 *
 * Se calcula con números y no con una librería de posicionamiento porque son
 * tres casos: es más código integrar la librería que resolverlo.
 */
function posicionDelCartel(recuadro: Recuadro | null): React.CSSProperties {
  const ANCHO = 384;
  const ALTO_APROX = 260;
  const BORDE = 16;

  if (!recuadro) {
    return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  }

  const alto = window.innerHeight;
  const ancho = window.innerWidth;

  const cabeDebajo = recuadro.top + recuadro.height + ALTO_APROX + BORDE < alto;
  const top = cabeDebajo
    ? recuadro.top + recuadro.height + 12
    : Math.max(BORDE, recuadro.top - ALTO_APROX - 12);

  // Se centra con el elemento, pero sin salirse por los lados de la pantalla.
  const centrado = recuadro.left + recuadro.width / 2 - ANCHO / 2;
  const left = Math.min(Math.max(BORDE, centrado), ancho - ANCHO - BORDE);

  return { top, left: Math.max(BORDE, left) };
}
